import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Settings, AlertTriangle, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import LightBackground from '@/components/LightBackground';
import { motion } from 'framer-motion';

type AcademyStatus = 'active' | 'maintenance' | 'coming_soon';

interface Academy {
  id: string;
  name: string;
  tagline: string | null;
  description: string;
  status: AcademyStatus;
  is_active: boolean;
  sort_order: number;
}

const statusConfig: Record<AcademyStatus, { label: string; icon: React.ReactNode; color: string; badgeClass: string }> = {
  active: {
    label: 'Active',
    icon: <CheckCircle className="w-4 h-4" />,
    color: 'text-green-600',
    badgeClass: 'bg-green-100 text-green-700 border-green-200',
  },
  maintenance: {
    label: 'In Maintenance',
    icon: <Settings className="w-4 h-4 animate-spin" />,
    color: 'text-amber-600',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  coming_soon: {
    label: 'Coming Soon',
    icon: <Clock className="w-4 h-4" />,
    color: 'text-blue-600',
    badgeClass: 'bg-blue-100 text-blue-700 border-blue-200',
  },
};

const AdminAcademies = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, isAdmin, authLoading, navigate]);

  useEffect(() => {
    fetchAcademies();
  }, []);

  const fetchAcademies = async () => {
    try {
      const { data, error } = await supabase
        .from('academies')
        .select('*')
        .order('sort_order');

      if (error) throw error;
      
      // Cast the status to our type since Supabase returns string
      const typedData = (data || []).map(academy => ({
        ...academy,
        status: (academy.status || 'active') as AcademyStatus,
      }));
      
      setAcademies(typedData);
    } catch (error) {
      console.error('Error fetching academies:', error);
      toast({
        title: 'Error',
        description: 'Failed to load academies',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateAcademyStatus = async (academyId: string, newStatus: AcademyStatus) => {
    setUpdating(academyId);
    try {
      const { error } = await supabase
        .from('academies')
        .update({ status: newStatus })
        .eq('id', academyId);

      if (error) throw error;

      setAcademies(prev =>
        prev.map(a => (a.id === academyId ? { ...a, status: newStatus } : a))
      );

      toast({
        title: 'Status Updated',
        description: `Academy status changed to ${statusConfig[newStatus].label}`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update academy status',
        variant: 'destructive',
      });
    } finally {
      setUpdating(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 relative z-10">
        {/* Header */}
        <motion.div 
          className="flex items-center gap-4 mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Academy <span className="text-primary">Management</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Control academy availability and access
            </p>
          </div>
        </motion.div>

        {/* Warning Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Card className="bg-amber-50 border-amber-200 mb-6 rounded-2xl">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">Important</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Changing an academy to "In Maintenance" or "Coming Soon" will immediately block access for all members. 
                    Only admins can view these academies.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Academies Grid */}
        <motion.div 
          className="grid gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {academies.map((academy, index) => {
            const config = statusConfig[academy.status];
            const isUpdating = updating === academy.id;

            return (
              <Card key={academy.id} className="bg-white border-border shadow-sm hover:shadow-md transition-shadow rounded-2xl">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground font-mono">
                            Academy {String(index + 1).padStart(2, '0')}
                          </span>
                          <Badge className={`${config.badgeClass} text-[10px] px-2 py-0.5`}>
                            <span className={`${config.color} flex items-center gap-1`}>
                              {config.icon}
                              {config.label}
                            </span>
                          </Badge>
                        </div>
                        <CardTitle className="text-lg font-semibold mt-1 truncate">
                          {academy.name}
                        </CardTitle>
                        {academy.tagline && (
                          <p className="text-sm text-primary/60 italic mt-1">{academy.tagline}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 sm:ml-auto">
                      <Select
                        value={academy.status}
                        onValueChange={(value: AcademyStatus) => updateAcademyStatus(academy.id, value)}
                        disabled={isUpdating}
                      >
                        <SelectTrigger className="w-[180px] bg-slate-50 border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-border">
                          <SelectItem value="active" className="focus:bg-slate-50">
                            <span className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Active
                            </span>
                          </SelectItem>
                          <SelectItem value="maintenance" className="focus:bg-slate-50">
                            <span className="flex items-center gap-2">
                              <Settings className="w-4 h-4 text-amber-600" />
                              In Maintenance
                            </span>
                          </SelectItem>
                          <SelectItem value="coming_soon" className="focus:bg-slate-50">
                            <span className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-blue-600" />
                              Coming Soon
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {academy.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </motion.div>

        {academies.length === 0 && !loading && (
          <Card className="bg-white border-border rounded-2xl">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No academies found</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminAcademies;
